// Tile staleness detection — the P0 safety property of a VMS live view.
//
// THE FAILURE THIS PREVENTS. On 2026-07-30 tiles rendered a picture for cameras
// that were NOT publishing: 019f0b59 and a8750635 showed frames while absent
// from every MediaMTX path, and a8750635 has no P1B binding at all so it has no
// path to publish on. An operator looking at a bank corridor cannot tell a live
// feed from a frame that is twenty minutes old. That is the worst failure this
// product can have, and it is worse than showing nothing.
//
// Two distinct mechanisms produced it, both addressed here plus in the player:
//   1. The player never cleared the <video> element when it went offline, so the
//      last decoded frame stayed on screen under a PARTIALLY transparent overlay.
//   2. There was no staleness detection at all: if the DB still said "online"
//      while the publisher died, hls.js happily played out its buffer and then
//      sat on the final frame in state "playing" — indefinitely, with no signal.
//
// WHY WE TRACK PLAYLIST PROGRESS, NOT currentTime. hls.js is configured with
// backBufferLength 90 and liveMaxLatencyDurationCount 6, so ``currentTime`` keeps
// advancing through buffered content for many seconds AFTER upstream dies. Only
// the arrival of a NEW fragment proves the publisher is still alive, so the
// liveness clock is reset by fragment append events, never by playback position.
//
// This module is intentionally pure so the thresholds can be tested exhaustively
// without a media element, matching the streamToken.ts / tileState.ts pattern.

/** MediaMTX serves `hlsSegmentDuration: 2s` (see deploy/ec2/mediamtx.yml). */
export const SEGMENT_DURATION_MS = 2_000;

/** `hlsSegmentCount: 6` × 2s ⇒ the live playlist window is 12s wide. */
export const PLAYLIST_WINDOW_MS = 6 * SEGMENT_DURATION_MS;

/**
 * No new fragment for this long ⇒ the tile is STALE and must stop showing video.
 *
 * Chosen as 10s = 5 × segment duration. The reasoning, against the actual
 * configuration rather than a round number:
 *   - a healthy stream appends a fragment every ~2s, so normal jitter across a
 *     segment boundary is well under 2s;
 *   - hls.js targets 3 segments behind live (`liveSyncDurationCount: 3` = 6s)
 *     and tolerates 6 (`liveMaxLatencyDurationCount` = 12s), so a legitimate gap
 *     can reach ~6s under load without anything being wrong — 10s clears that
 *     ceiling with margin and will not false-positive on a slow segment;
 *   - it is BELOW the 12s full-playlist drain, so we flip the tile before the
 *     playlist has even emptied, rather than after;
 *   - and it is far below the interval over which a frozen frame becomes
 *     dangerous to an operator.
 * Raising this trades operator safety for fewer transient "unavailable" flickers;
 * lowering it below ~7s risks false positives at the configured latency ceiling.
 */
export const STALE_AFTER_MS = 5 * SEGMENT_DURATION_MS;

/**
 * How long to allow for the first fragment before calling it a failed start.
 * Manifest fetch + first segment + decode is normally 2-4s; the transcode path
 * is slower because `runOnDemandStartTimeout` on the `_h264` sibling is 30s and
 * ffmpeg must wait for an IDR. We deliberately do NOT wait the full 30s here:
 * the tile shows an explicit "starting" state during the grace period, so a long
 * transcode spin-up is visible as *starting*, never as live video.
 */
export const STARTUP_GRACE_MS = 15_000;

export type StreamLiveness =
  /** No fragment yet, still inside the startup grace — show a starting state. */
  | { kind: "starting"; waitingForMs: number }
  /** No fragment ever arrived and the grace elapsed — distinct from went-stale. */
  | { kind: "never-started"; waitingForMs: number }
  /** A fragment arrived recently enough to trust the picture. */
  | { kind: "live" }
  /** Was live, then the playlist stopped advancing — CLEAR THE PICTURE. */
  | { kind: "stale"; staleForMs: number };

export interface LivenessInput {
  /** Epoch ms when the player began loading this source. */
  startedAt: number;
  /** Epoch ms of the most recent NEW fragment append, or null if none yet. */
  lastFragmentAt: number | null;
  /** Now, injected so this stays pure and testable. */
  now: number;
  staleAfterMs?: number;
  startupGraceMs?: number;
}

/**
 * Classify a stream's liveness. The caller renders video ONLY for `live`.
 *
 * `never-started` and `stale` are kept separate on purpose: they are different
 * operational problems (nothing was ever published on this path vs the publisher
 * died mid-stream) and the operator should not have to guess which one they are
 * looking at.
 */
export function evaluateLiveness({
  startedAt,
  lastFragmentAt,
  now,
  staleAfterMs = STALE_AFTER_MS,
  startupGraceMs = STARTUP_GRACE_MS,
}: LivenessInput): StreamLiveness {
  if (lastFragmentAt === null) {
    const waitingForMs = Math.max(0, now - startedAt);
    return waitingForMs >= startupGraceMs
      ? { kind: "never-started", waitingForMs }
      : { kind: "starting", waitingForMs };
  }
  const sinceFragment = Math.max(0, now - lastFragmentAt);
  if (sinceFragment >= staleAfterMs) {
    return { kind: "stale", staleForMs: sinceFragment };
  }
  return { kind: "live" };
}

/** True when the picture must be cleared — never hold a frame in these states. */
export function mustClearPicture(liveness: StreamLiveness): boolean {
  return liveness.kind === "stale" || liveness.kind === "never-started";
}

/** Operator-facing copy. Distinguishes the two failure modes deliberately. */
export function livenessMessage(liveness: StreamLiveness): string | null {
  switch (liveness.kind) {
    case "live":
      return null;
    case "starting":
      return "Starting stream…";
    case "never-started":
      return "Stream unavailable";
    case "stale":
      return "Stream unavailable — feed stopped";
  }
}
